import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { giftCardApi } from '../../services/giftCardApi';

interface TierRow {
  id?: string; // existing tier — round-trip on edit so the backend preserves it
  pay: string; // dollars
  value: string; // dollars
}

export default function AdminGiftCardPromotionForm() {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const { isAuthenticated, getToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    triggerType: 'SITE_WIDE',
    code: '',
    perEmailLimit: '',
    usageLimit: '',
    validFrom: '',
    validUntil: '',
    isActive: true,
  });
  // "Pay X, get Y" tiers — at least one required.
  const [tiers, setTiers] = useState<TierRow[]>([{ pay: '', value: '' }]);

  useEffect(() => {
    if (!isEditing || !isAuthenticated) return;
    (async () => {
      const token = await getToken();
      if (!token) return;
      const p = await giftCardApi.adminGetPromotion(id!, token);
      // Format an ISO timestamp into a local `datetime-local` value so the
      // displayed window matches what was saved (submit converts back via toISOString).
      const toLocalDatetime = (dateStr: string | null) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      setForm({
        name: p.name,
        description: p.description || '',
        triggerType: p.triggerType,
        code: p.code || '',
        perEmailLimit: p.perEmailLimit ? String(p.perEmailLimit) : '',
        usageLimit: p.usageLimit ? String(p.usageLimit) : '',
        validFrom: toLocalDatetime(p.validFrom),
        validUntil: toLocalDatetime(p.validUntil),
        isActive: p.isActive,
      });
      setTiers(
        p.tiers.length
          ? p.tiers.map((t) => ({
              id: t.id,
              pay: String(t.payAmount / 100),
              value: String(t.faceValue / 100),
            }))
          : [{ pay: '', value: '' }]
      );
    })();
  }, [isEditing, id, isAuthenticated, getToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError('Name is required');
    if (form.triggerType === 'PROMO_CODE' && !form.code.trim())
      return setError('Code required for promo-code trigger');

    const parsedTiers = tiers
      .filter((t) => t.pay || t.value)
      .map((t) => ({
        ...(t.id ? { id: t.id } : {}),
        payAmount: Math.round(Number(t.pay) * 100),
        faceValue: Math.round(Number(t.value) * 100),
      }));
    if (parsedTiers.length === 0) return setError('Add at least one offer (pay X, get Y)');
    for (const [i, t] of parsedTiers.entries()) {
      if (!Number.isFinite(t.payAmount) || t.payAmount <= 0)
        return setError(`Offer ${i + 1}: pay amount must be > 0`);
      if (!Number.isFinite(t.faceValue) || t.faceValue <= t.payAmount)
        return setError(`Offer ${i + 1}: card value must be greater than the pay amount`);
    }

    const payload: Record<string, unknown> = {
      name: form.name,
      description: form.description || undefined,
      triggerType: form.triggerType,
      code: form.triggerType === 'PROMO_CODE' ? form.code : null,
      perEmailLimit: form.perEmailLimit ? Number(form.perEmailLimit) : null,
      usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
      validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
      tiers: parsedTiers,
    };

    try {
      setSubmitting(true);
      const token = await getToken();
      if (!token) throw new Error('No access token');
      if (isEditing) {
        payload.isActive = form.isActive;
        await giftCardApi.adminUpdatePromotion(id!, payload, token);
      } else {
        await giftCardApi.adminCreatePromotion(payload, token);
      }
      navigate('/admin/gift-cards');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const setTier = (i: number, k: keyof TierRow, v: string) =>
    setTiers((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addTier = () => setTiers((rows) => [...rows, { pay: '', value: '' }]);
  const removeTier = (i: number) =>
    setTiers((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary';

  return (
    <AdminLayout title={isEditing ? 'Edit Gift Card Promotion' : 'New Gift Card Promotion'}>
      {error && (
        <div className="mb-4 max-w-xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm p-6 space-y-4 max-w-xl mx-auto"
      >
        <div>
          <label className="block text-sm font-medium mb-1">Name *</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description (shown to users)</label>
          <input
            className={inputCls}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Trigger</label>
          <select
            className={inputCls}
            value={form.triggerType}
            onChange={(e) => set('triggerType', e.target.value)}
          >
            <option value="SITE_WIDE">Site-wide (auto)</option>
            <option value="PROMO_CODE">Promo code</option>
          </select>
        </div>
        {form.triggerType === 'PROMO_CODE' && (
          <div>
            <label className="block text-sm font-medium mb-1">Code *</label>
            <input
              className={`${inputCls} font-mono uppercase`}
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
            />
          </div>
        )}

        {/* Offers: pay X, get Y */}
        <div>
          <label className="block text-sm font-medium mb-1">Offers (pay X, get Y) *</label>
          <p className="text-xs text-gray-500 mb-2">
            e.g. pay $85, get $100. Add multiple offers for different amounts.
          </p>
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-600 whitespace-nowrap">Pay $</span>
                <input
                  type="number"
                  min="1"
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  value={t.pay}
                  onChange={(e) => setTier(i, 'pay', e.target.value)}
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">Get $</span>
                <input
                  type="number"
                  min="1"
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  value={t.value}
                  onChange={(e) => setTier(i, 'value', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeTier(i)}
                  disabled={tiers.length === 1}
                  className="ml-auto p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                  aria-label="Remove offer"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addTier}
            className="mt-2 text-sm text-primary font-medium hover:underline"
          >
            + Add offer
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Per-email limit</label>
            <input
              type="number"
              className={inputCls}
              value={form.perEmailLimit}
              onChange={(e) => set('perEmailLimit', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Total usage limit</label>
            <input
              type="number"
              className={inputCls}
              value={form.usageLimit}
              onChange={(e) => set('usageLimit', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Valid from</label>
            <input
              type="datetime-local"
              className={inputCls}
              value={form.validFrom}
              onChange={(e) => set('validFrom', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Valid until</label>
            <input
              type="datetime-local"
              className={inputCls}
              value={form.validUntil}
              onChange={(e) => set('validUntil', e.target.value)}
            />
          </div>
        </div>
        {isEditing && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            <span className="text-sm">Active</span>
          </label>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="py-3 px-6 rounded-lg bg-primary text-white font-semibold disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </form>
    </AdminLayout>
  );
}
